"use client";

import { useCallback } from "react";
import { UseFormReturn, FieldPath, FieldValues } from "react-hook-form";
import { getIndefiniteArticle, prettifyCamelCase } from "@/lib/utils";

export type FieldDetectionConfig<T = Record<string, any>> = {
  [K in keyof T]: string[];
};

export type DetectionResult<T = Record<string, any>> = {
  [K in keyof T]?: string;
};

// Takes a config object and a form and returns a function to update the form from a list of fields
// The config object is a mapping of field names to an array of possible field names
// The function will detect the field names from the list of fields and update the form
// If a field is not detected, it will set an error on the form
export function useCSVFieldDetection<T extends FieldValues>(
  config: FieldDetectionConfig<T>,
  form: UseFormReturn<T>
) {
  const detectFields = useCallback(
    (fields: string[]) => {
      const result: DetectionResult<T> = {};
      if (!fields) return result;

      const normalizeField = (field: string) =>
        field.toLowerCase().replace(/_/g, " ").replace(/\s+/g, " ").trim();

      fields.forEach(field => {
        const normalized = normalizeField(field);

        Object.entries(config).forEach(([key, patterns]) => {
          if (patterns.includes(normalized)) {
            result[key as keyof T] = field;
          }
        });
      });

      return result;
    },
    [config]
  );

  const updateFormFromDetected = useCallback(
    (fields: string[]) => {
      const detected = detectFields(fields);
      const formValues = form.getValues();

      Object.entries(formValues).forEach(([key]) => {
        const detectedValue = detected[key as keyof T];

        if (detectedValue) {
          form.setValue(key as FieldPath<T>, detectedValue as any);
          form.clearErrors(key as FieldPath<T>);
        } else {
          form.setError(key as FieldPath<T>, {
            type: "manual",
            message: `Select ${getIndefiniteArticle(key)} ${prettifyCamelCase(key).toLowerCase()} column`,
          });
        }
      });
    },
    [detectFields, form]
  );

  return {
    detectFields,
    updateFormFromDetected,
  };
}
